import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { title: "", location: "", category: "", description: "", before: "", after: "" };

export const AdminPortfolio = () => {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState(EMPTY);
    const [busy, setBusy] = useState(false);
    const beforeRef = useRef(null);
    const afterRef = useRef(null);

    const load = () =>
        axios.get(`${API}/projects`).then((r) => setItems(r.data.projects || [])).catch(() => {});

    useEffect(() => { load(); }, []);

    const pickImage = (field) => async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        if (f.size > 3 * 1024 * 1024) { toast.error("Photo trop lourde (max 3 Mo)"); return; }
        const dataUri = await new Promise((res) => {
            const r = new FileReader();
            r.onload = () => res(r.result);
            r.readAsDataURL(f);
        });
        setForm((prev) => ({ ...prev, [field]: dataUri }));
        toast.success(`Photo « ${field === "before" ? "avant" : "après"} » chargée`);
    };

    const add = async (e) => {
        e.preventDefault();
        if (!form.before || !form.after) { toast.error("Chargez les photos avant ET après"); return; }
        setBusy(true);
        try {
            await axios.post(`${API}/admin/portfolio`, form);
            setForm(EMPTY);
            toast.success("Réalisation publiée ✓ (visible sur la home et /realisations)");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur de publication");
        } finally {
            setBusy(false);
        }
    };

    const del = async (id) => {
        if (!window.confirm("Supprimer cette réalisation ?")) return;
        await axios.delete(`${API}/admin/portfolio/${id}`).catch(() => {});
        toast.success("Réalisation supprimée");
        load();
    };

    return (
        <div className="space-y-6">
            <form onSubmit={add} className="lux-card p-6 space-y-3">
                <div className="tracking-luxe text-[#B89C6A]">Publier une réalisation (avant / après)</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <input data-testid="portfolio-title" className="lux-input" placeholder="Titre * (ex: Appartement 95m²)" required
                        value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                    <input data-testid="portfolio-location" className="lux-input" placeholder="Lieu (ex: Paris 15ème)"
                        value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                    <input data-testid="portfolio-category" className="lux-input" placeholder="Catégorie (ex: Rénovation complète)"
                        value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
                </div>
                <textarea data-testid="portfolio-description" className="lux-input w-full" rows={2}
                    placeholder="Description des travaux réalisés"
                    value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                <div className="grid grid-cols-2 gap-3">
                    <input ref={beforeRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pickImage("before")} />
                    <input ref={afterRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pickImage("after")} />
                    <button data-testid="portfolio-before-pick" type="button" onClick={() => beforeRef.current?.click()}
                        className={`btn-outline justify-center ${form.before ? "border-[#B89C6A] text-[#B89C6A]" : ""}`}>
                        {form.before ? "✓ Photo avant chargée" : "Photo AVANT *"}
                    </button>
                    <button data-testid="portfolio-after-pick" type="button" onClick={() => afterRef.current?.click()}
                        className={`btn-outline justify-center ${form.after ? "border-[#B89C6A] text-[#B89C6A]" : ""}`}>
                        {form.after ? "✓ Photo après chargée" : "Photo APRÈS *"}
                    </button>
                </div>
                {(form.before || form.after) && (
                    <div className="flex gap-3">
                        {form.before && <img src={form.before} alt="Avant" className="h-24 w-32 object-cover border hairline" />}
                        {form.after && <img src={form.after} alt="Après" className="h-24 w-32 object-cover border hairline" />}
                    </div>
                )}
                <button data-testid="portfolio-add" type="submit" disabled={busy} className="btn-champagne disabled:opacity-50">
                    {busy ? "Publication…" : "Publier la réalisation"}
                </button>
            </form>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {items.map((p) => (
                    <div key={p.id} data-testid={`portfolio-item-${p.id}`} className="lux-card p-4 flex gap-4">
                        <img src={p.after} alt={p.title} className="h-20 w-24 object-cover border hairline shrink-0" />
                        <div className="min-w-0 flex-1">
                            <div className="font-serif-lux text-lg leading-tight">{p.title}</div>
                            <div className="text-[11px] text-[#B89C6A] mt-0.5">{[p.category, p.location].filter(Boolean).join(" · ")}</div>
                            <button data-testid={`portfolio-delete-${p.id}`} onClick={() => del(p.id)}
                                className="mt-2 text-[11px] tracking-wider uppercase text-red-700/70 hover:text-red-700 underline">
                                Supprimer
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
